import { API_CACHE_KEYS } from "api/cacheKeys";
import { ApiError, BaseQueryOptions, CachePolicy } from "api/types";
import { useCustomMutation } from "api/useCustomMutation";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import { normalizeBox } from "features/canvas/components/ShapeRenderer";
import { ListResponse } from "types/api";
import { CanvasAnnotation, ROI, SimpleBEROI } from "types/imageAnnotations";
import { ANSWER_VALUES, DisplayBBoxInfo, REVIEW_REASON_CHOICES, ReviewBboxDisplayChoices } from "types/imageQueries";
import { LabelValue } from "types/labelValue";
import { clampFloatingPointErrors } from "utils/math";

type annotationsRequested = "BINARY_CLASSIFICATION" | "BOUNDING_BOXES,BINARY_CLASSIFICATION";

export interface CreateLabelValueData {
  posicheck_id: string;
  label: string;
  feedback_text?: string;
  annotations_requested?: annotationsRequested;
  rois?: CanvasAnnotation[] | undefined | null;
  elapsed_screen_time_ms?: number;
  review_reason: REVIEW_REASON_CHOICES;
  review_bbox_info?: DisplayBBoxInfo;
  selected_label_value?: LabelValue | null;
}

type CreateLabelValueBackendPayload = {
  annotations_requested?: annotationsRequested;
  posicheck_id: string;
  label: string;
  feedback_text?: string;
  rois?: SimpleBEROI[] | undefined | null;
  elapsed_screen_time_ms?: number;
  review_reason: REVIEW_REASON_CHOICES;
  displayed_rois: ROI[] | null;
  review_bbox_display?: ReviewBboxDisplayChoices;
};

function useGetImageQueryLabels(imageQueryId?: string, options: BaseQueryOptions<ListResponse<LabelValue>> = {}) {
  const url = `${JANZU_URL}reef-api/labels?iq_id=${imageQueryId}&page_size=50`;
  return useCustomQuery<ListResponse<LabelValue>>(API_CACHE_KEYS.labels.list(imageQueryId), url, {
    cachePolicy: CachePolicy.NO_CACHE,
    enabled: !!imageQueryId,
    ...options,
  });
}

function useCreateLabel() {
  return useCustomMutation<LabelValue, CreateLabelValueBackendPayload>(`${JANZU_URL}device-api/labels`, "POST", {
    cacheKeysToInvalidate: [API_CACHE_KEYS.labels.all],
  });
}

export type AcceptUnclearLabelsData = {
  detector_id: string;
};

function useAcceptUnclearLabels() {
  return useCustomMutation<void, AcceptUnclearLabelsData>(`${JANZU_URL}device-api/labels/acceptunclear`, "PUT", {
    cacheKeysToInvalidate: [API_CACHE_KEYS.labels.all],
  });
}

export function getBackendPayloadFromFrontendPayload(data: CreateLabelValueData): CreateLabelValueBackendPayload {
  const {
    rois: dataRois,
    review_bbox_info: reviewBBoxInfo,
    selected_label_value: selectedLabelValue,
    ...payload
  } = data;

  const rois: SimpleBEROI[] | undefined = dataRois?.map((roi) => {
    const { bounds } = normalizeBox(roi.start, roi.end);

    const clampedBounds = {
      left: clampFloatingPointErrors(bounds.left),
      right: clampFloatingPointErrors(bounds.right),
      top: clampFloatingPointErrors(bounds.top),
      bottom: clampFloatingPointErrors(bounds.bottom),
    };

    return {
      label: roi.label,
      score: 1, // Hardcoded to 1 because user-drawn bounding boxes are assumed to have full confidence
      geometry: {
        ...clampedBounds,
        x: clampedBounds.left,
        y: clampedBounds.top,
      },
    };
  });

  // eslint-disable-next-line no-underscore-dangle
  const unsureLabels = [ANSWER_VALUES.UNCLEAR, ANSWER_VALUES.__UNSURE, ANSWER_VALUES.UNSURE];
  const shouldSendRois = rois && !unsureLabels.includes(data.label as ANSWER_VALUES);
  const annotationsRequested = shouldSendRois ? "BOUNDING_BOXES,BINARY_CLASSIFICATION" : "BINARY_CLASSIFICATION";

  return {
    ...payload,
    annotations_requested: annotationsRequested,
    // always send an array and on unclear send nothing
    ...(shouldSendRois && { rois }),
    ...(reviewBBoxInfo
      ? {
          review_bbox_display: reviewBBoxInfo.display_bbox,
          displayed_rois: reviewBBoxInfo.display_bbox_rois,
        }
      : {
          review_bbox_display: ReviewBboxDisplayChoices.UNKNOWN_DEFAULT,
          displayed_rois: selectedLabelValue?.rois ?? [],
        }),
  };
}

type UseLabelsReturn = {
  // Get operations
  labels: ListResponse<LabelValue> | undefined;
  getLabels: () => Promise<ListResponse<LabelValue>>;
  isLoadingLabels: boolean;
  labelsError: ApiError | null;

  // Create operations
  createLabel: (data: CreateLabelValueData) => void;
  createLabelAsync: (data: CreateLabelValueData) => Promise<LabelValue>;
  isCreatingLabel: boolean;
  createLabelError: ApiError | null;

  // Accept unclear operations
  acceptUnclearLabels: (data: AcceptUnclearLabelsData) => void;
  acceptUnclearLabelsAsync: (data: AcceptUnclearLabelsData) => Promise<void>;
  isAcceptingUnclearLabels: boolean;
  acceptUnclearLabelsError: ApiError | null;

  isLoading: boolean;
};

export function useLabels(
  imageQueryId?: string,
  getOptions: BaseQueryOptions<ListResponse<LabelValue>> = {}
): UseLabelsReturn {
  const createLabelMutation = useCreateLabel();
  const acceptUnclearMutation = useAcceptUnclearLabels();
  const getLabelsQuery = useGetImageQueryLabels(imageQueryId, getOptions);

  return {
    // Get operations
    labels: getLabelsQuery.data,
    getLabels: getLabelsQuery.refetch,
    isLoadingLabels: getLabelsQuery.isFetching,
    labelsError: getLabelsQuery.error,

    // Create operation
    createLabel: (data: CreateLabelValueData) => createLabelMutation.mutate(getBackendPayloadFromFrontendPayload(data)),
    createLabelAsync: (data: CreateLabelValueData) =>
      createLabelMutation.mutateAsync(getBackendPayloadFromFrontendPayload(data)),
    isCreatingLabel: createLabelMutation.isLoading,
    createLabelError: createLabelMutation.error,

    // Accept unclear operations
    acceptUnclearLabels: (data: AcceptUnclearLabelsData) => acceptUnclearMutation.mutate(data),
    acceptUnclearLabelsAsync: (data: AcceptUnclearLabelsData) => acceptUnclearMutation.mutateAsync(data),
    isAcceptingUnclearLabels: acceptUnclearMutation.isLoading,
    acceptUnclearLabelsError: acceptUnclearMutation.error,

    // Combined loading state
    isLoading: createLabelMutation.isLoading || acceptUnclearMutation.isLoading || getLabelsQuery.isFetching,
  };
}
